import React, { useContext, createContext, type InputHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import { css, cx } from '@emotion/css';
import { IoCheckmark } from 'react-icons/io5';

/* Context */
interface RadioContextValue {
  value: string | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const RadioContext = createContext<RadioContextValue | undefined>(undefined);

/* Styles */
const optionButtonBase = css({
  cursor: 'pointer',
  display: 'flex',
  position: 'relative',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  padding: 'var(--spacing-sm)',
  borderRadius: 'var(--spacing-md)',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  backgroundColor: 'var(--primary-color)',
  color: 'var(--pc-700)',
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',

  '&:hover': {
    borderColor: 'var(--pc-300)',
    backgroundColor: 'var(--pc-100)',
    boxShadow: 'var(--shadow-200)',
  },
});

const optionButtonChecked = css({
  backgroundColor: 'var(--pc-100)',
  borderColor: 'var(--pc-400)',
  color: 'var(--pc-700)',
  boxShadow: 'var(--shadow-200)',

  '&:hover': {
    backgroundColor: 'var(--pc-100)',
    borderColor: 'var(--pc-500)',
  },
});

const optionButtonUnchecked = css({
  backgroundColor: 'var(--primary-color)',
  color: 'var(--pc-700)',
});

const radioCheckmarkWrapper = css({
  position: 'absolute',
  width: '50px',
  height: '50px',
  left: '0%',
  top: '0%',
  backgroundColor: 'var(--pc-500)',
  clipPath: 'polygon(0 0, 0% 80%, 100% 0)',
});

const radioCheckmark = css({
  position: 'relative',
  left: '12%',
  top: '7%',
  color: 'var(--pc-100)',

  '& > svg': {
    height: '20px',
    width: '20px',
  },
});

const radioInput = css({
  display: 'none',
});

/* Components */
export interface OptionButtonProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

export const OptionButton: React.FC<OptionButtonProps> = ({ 
  className = '', 
  children, 
  style, 
  value,
  ...props 
}) => {
  const context = useContext(RadioContext);
  
  if (!context) {
    throw new Error('OptionButton must be used within OptionButtonGroup');
  }

  const { value: selectedValue, onChange } = context;
  const isChecked = selectedValue === value;

  return (
    <label
      className={cx(
        optionButtonBase,
        isChecked ? optionButtonChecked : optionButtonUnchecked,
        className
      )}
      style={style}
    >
      {isChecked && (
        <div className={radioCheckmarkWrapper}>
          <div className={radioCheckmark}>
            <IoCheckmark />
          </div>
        </div>
      )}
      <input
        type="radio"
        className={radioInput}
        checked={isChecked}
        onChange={onChange}
        value={value}
        {...props}
      />
      {children}
    </label>
  );
};

export interface OptionButtonGroupProps {
  value: string | undefined;
  onChange: (value: string) => void;
  children: ReactNode;
}

export const OptionButtonGroup: React.FC<OptionButtonGroupProps> = ({ 
  value, 
  onChange, 
  children 
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <RadioContext.Provider value={{ value, onChange: handleChange }}>
      {children}
    </RadioContext.Provider>
  );
};
